'use client'

import Link from 'next/link'
import { Star } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  symptoms,
  BOOKING_URL,
} from '@/lib/attention-deficit-hyperactivity-disorder-therapy-data'
import {
  Pill,
  Bullet,
  AssessmentButton,
} from '@/components/attention-deficit-hyperactivity-disorder-therapy/shared'

const HeroSection: React.FC = () => {
  return (
    <div className="relative isolate overflow-hidden bg-gradient-to-b from-emerald-50/40 to-background">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 md:pt-24 pb-8 sm:pb-10">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-10">
          {/* Left column */}
          <div className="w-full lg:w-3/5">
            <Badge variant="secondary" className="mb-3 sm:mb-4 text-xs">
              Adult Program
            </Badge>

            <h1 className="text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight mb-3 sm:mb-4">
              ADHD Therapy for Adults
            </h1>

            <p className="text-sm sm:text-base lg:text-lg text-muted-foreground mb-4 sm:mb-6 max-w-2xl">
              Specialist-led adult ADHD care at ReACH Psychiatry in Bangalore —
              a structured, evidence-based program combining coaching, CBT
              techniques and medication management to help you reduce overwhelm,
              stay on task, and feel in control.
            </p>

            {/* Symptom pills */}
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8">
              {symptoms.map((s) => (
                <Pill key={s.label} icon={s.icon}>
                  {s.label}
                </Pill>
              ))}
            </div>

            {/* CTA cluster */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link href={BOOKING_URL}>Book an Assessment</Link>
              </Button>
              <AssessmentButton />
              <Button
                asChild
                variant="outline"
                size="lg"
                className="w-full sm:w-auto"
              >
                <Link href="#program">See the 15-session plan</Link>
              </Button>
            </div>
          </div>

          {/* Right column */}
          <div className="w-full lg:w-2/5">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Star className="h-4 w-4 sm:h-5 sm:w-5" />
                  What you get
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 sm:space-y-3">
                  <Bullet>
                    Personalized plan after psychiatrist + psychologist review
                  </Bullet>
                  <Bullet>Weekly sessions (50-60 min) with clear goals</Bullet>
                  <Bullet>
                    Proven tools for organization, prioritization & time
                  </Bullet>
                  <Bullet>
                    Follow-ups for medication management as needed
                  </Bullet>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HeroSection
